import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  FileText,
  Calendar,
  DollarSign,
  MapPin,
  Clock,
  MessageSquare,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  X,
  Upload,
  Camera,
  Video,
  BookOpen,
  TrendingUp,
  Globe,
  Palette,
  Folder
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const ClientRequestsPage = () => {
  const { api, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [stats, setStats] = useState({ total_requests: 0, pending_quotes: 0, upcoming_bookings: 0 });

  const [formData, setFormData] = useState({
    service_category: '',
    package_id: '',
    title: '',
    description: '',
    location: '',
    preferred_dates: [],
    budget_range: '',
    attachments: []
  });
  const [dateInput, setDateInput] = useState('');

  const iconMap = {
    'camera': Camera,
    'video': Video,
    'book-open': BookOpen,
    'trending-up': TrendingUp,
    'globe': Globe,
    'palette': Palette,
    'folder': Folder
  };

  const getIconComponent = (iconId) => {
    return iconMap[iconId] || Folder;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, categoriesRes, statusesRes, packagesRes, statsRes] = await Promise.all([
        api.get('/requests'),
        api.get('/service-categories'),
        api.get('/request-statuses'),
        api.get('/packages'),
        api.get('/stats/client')
      ]);
      setRequests(requestsRes.data);
      setCategories(categoriesRes.data.filter(c => c.active));
      setStatuses(statusesRes.data);
      setPackages(packagesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!formData.service_category || !formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.post('/requests', formData);
      toast.success('Request submitted successfully!');
      setNewRequestOpen(false);
      setFormData({
        service_category: '',
        package_id: '',
        title: '',
        description: '',
        location: '',
        preferred_dates: [],
        budget_range: '',
        attachments: []
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create request:', error);
      toast.error('Failed to submit request');
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedRequest) return;
    try {
      await api.post(`/requests/${selectedRequest.id}/comments`, {
        text: newComment,
        is_internal: false
      });
      const response = await api.get(`/requests/${selectedRequest.id}`);
      setSelectedRequest(response.data);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const respondToQuote = async (action, feedback = '') => {
    if (!selectedRequest?.quote) return;
    try {
      await api.patch(`/quotes/${selectedRequest.quote.id}/respond?action=${action}`, null, {
        params: { feedback }
      });
      toast.success(action === 'approve' ? 'Quote approved!' : 'Response submitted');
      fetchData();
      const response = await api.get(`/requests/${selectedRequest.id}`);
      setSelectedRequest(response.data);
    } catch (error) {
      console.error('Failed to respond to quote:', error);
      toast.error('Failed to submit response');
    }
  };

  const addPreferredDate = () => {
    if (dateInput && !formData.preferred_dates.includes(dateInput)) {
      setFormData({
        ...formData,
        preferred_dates: [...formData.preferred_dates, dateInput]
      });
      setDateInput('');
    }
  };

  const removePreferredDate = (date) => {
    setFormData({
      ...formData,
      preferred_dates: formData.preferred_dates.filter(d => d !== date)
    });
  };

  const getStatusColor = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    const colors = {
      blue: 'bg-blue-500',
      yellow: 'bg-yellow-500',
      purple: 'bg-purple-500',
      green: 'bg-green-500',
      cyan: 'bg-cyan-500',
      orange: 'bg-orange-500',
      teal: 'bg-teal-500',
      gray: 'bg-gray-500'
    };
    return colors[status?.color] || 'bg-gray-500';
  };

  const getStatusLabel = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    return status?.label || statusId;
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = req.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.request_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getCategoryPackages = (categoryId) => {
    return packages.filter(p => p.service_category === categoryId);
  };

  return (
    <ClientLayout>
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-fp-surface rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-fp-textMuted text-sm">Total Requests</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.total_requests}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
            </div>
          </div>
          <div className="bg-fp-surface rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-fp-textMuted text-sm">Pending Quotes</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.pending_quotes}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </div>
          <div className="bg-fp-surface rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-fp-textMuted text-sm">Upcoming Bookings</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.upcoming_bookings}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="client-requests-title">My Service Requests</h1>
            <p className="text-fp-textMuted text-sm mt-1">Track and manage your project requests</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fp-textMuted" />
              <Input
                placeholder="Search requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-fp-surface border-white/10 text-white w-64"
                data-testid="client-requests-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-fp-surface border-white/10 text-white">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-fp-surface border-white/10">
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map(status => (
                  <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setNewRequestOpen(true)} className="btn-primary" data-testid="new-request-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12 text-fp-textMuted">Loading...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-12 bg-fp-surface rounded-xl border border-white/10">
            <FileText className="w-12 h-12 text-fp-textMuted mx-auto mb-4" />
            <p className="text-white text-lg mb-2">No requests yet</p>
            <p className="text-fp-textMuted mb-4">Create your first service request to get started</p>
            <Button onClick={() => setNewRequestOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => (
              <motion.div
                key={request.id}
                className="bg-fp-surface rounded-xl border border-white/10 p-6 cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => {
                  setSelectedRequest(request);
                  setDetailOpen(true);
                }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`client-request-card-${request.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-fp-textMuted text-sm">{request.request_number}</span>
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                        {getCategoryName(request.service_category)}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{request.title}</h3>
                    <p className="text-fp-textMuted text-sm line-clamp-2">{request.description}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${getStatusColor(request.status)}`} />
                        <span className="text-white capitalize">{getStatusLabel(request.status)}</span>
                      </div>
                      <p className="text-fp-textMuted text-sm mt-1">{formatDate(request.created_at)}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-fp-textMuted" />
                  </div>
                </div>
                
                {/* Quote Banner */}
                {request.quote && request.quote.status === 'sent' && (
                  <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-yellow-400" />
                      <div>
                        <p className="text-yellow-400 font-medium">Quote Pending Review</p>
                        <p className="text-fp-textMuted text-sm">Total: {formatCurrency(request.quote.grand_total)}</p>
                      </div>
                    </div>
                    <span className="text-yellow-400 text-sm">Click to review</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* New Request Dialog */}
        <Dialog open={newRequestOpen} onOpenChange={setNewRequestOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">New Service Request</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Service Category Selection */}
              <div>
                <label className="text-fp-textMuted text-xs block mb-3">Select Service *</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {categories.map((category) => {
                    const IconComp = getIconComponent(category.icon);
                    return (
                      <button
                        key={category.id}
                        onClick={() => setFormData({ ...formData, service_category: category.id, package_id: '' })}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          formData.service_category === category.id
                            ? 'border-primary bg-primary/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <IconComp className={`w-6 h-6 mb-2 ${formData.service_category === category.id ? 'text-primary' : 'text-fp-textMuted'}`} />
                        <p className="text-white text-sm font-medium">{category.name}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Package Selection (if category selected) */}
              {formData.service_category && getCategoryPackages(formData.service_category).length > 0 && (
                <div>
                  <label className="text-fp-textMuted text-xs block mb-3">Select Package (Optional)</label>
                  <div className="space-y-2">
                    {getCategoryPackages(formData.service_category).map((pkg) => (
                      <button
                        key={pkg.id}
                        onClick={() => setFormData({ ...formData, package_id: pkg.id })}
                        className={`w-full p-4 rounded-xl border text-left transition-all ${
                          formData.package_id === pkg.id
                            ? 'border-primary bg-primary/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{pkg.name}</p>
                            <p className="text-fp-textMuted text-sm mt-1">{pkg.description}</p>
                            {pkg.features?.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {pkg.features.slice(0, 3).map((feature, i) => (
                                  <li key={i} className="text-fp-textMuted text-xs flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3 text-green-400" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <p className="text-primary font-bold">{formatCurrency(pkg.base_price)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Request Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-fp-textMuted text-xs block mb-2">Project Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Corporate Video for Annual Report"
                    className="bg-fp-bg border-white/10 text-white"
                    data-testid="request-title-input"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="text-fp-textMuted text-xs block mb-2">Description *</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your project requirements in detail..."
                    className="bg-fp-bg border-white/10 text-white resize-none"
                    rows={4}
                    data-testid="request-description-input"
                  />
                </div>
                
                <div>
                  <label className="text-fp-textMuted text-xs block mb-2">Location</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Mumbai, Pune"
                    className="bg-fp-bg border-white/10 text-white"
                  />
                </div>
                
                <div>
                  <label className="text-fp-textMuted text-xs block mb-2">Budget Range</label>
                  <Select 
                    value={formData.budget_range} 
                    onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                  >
                    <SelectTrigger className="bg-fp-bg border-white/10 text-white">
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent className="bg-fp-surface border-white/10">
                      <SelectItem value="10000-25000">₹10,000 - ₹25,000</SelectItem>
                      <SelectItem value="25000-50000">₹25,000 - ₹50,000</SelectItem>
                      <SelectItem value="50000-100000">₹50,000 - ₹1,00,000</SelectItem>
                      <SelectItem value="100000-250000">₹1,00,000 - ₹2,50,000</SelectItem>
                      <SelectItem value="250000+">₹2,50,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="text-fp-textMuted text-xs block mb-2">Preferred Dates</label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      type="date"
                      value={dateInput}
                      onChange={(e) => setDateInput(e.target.value)}
                      className="bg-fp-bg border-white/10 text-white flex-1"
                    />
                    <Button onClick={addPreferredDate} variant="outline" className="border-white/10">
                      Add
                    </Button>
                  </div>
                  {formData.preferred_dates.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.preferred_dates.map((date) => (
                        <span 
                          key={date}
                          className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full flex items-center gap-2"
                        >
                          {date}
                          <button onClick={() => removePreferredDate(date)} className="hover:text-red-400">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="outline" onClick={() => setNewRequestOpen(false)} className="border-white/10">
                  Cancel
                </Button>
                <Button onClick={handleCreateRequest} className="btn-primary" data-testid="submit-request-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Submit Request
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Request Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Request Details</DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-fp-textMuted text-sm">{selectedRequest.request_number}</p>
                    <h2 className="text-xl font-semibold text-white mt-1">{selectedRequest.title}</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(selectedRequest.status)}`} />
                    <span className="text-white capitalize">{getStatusLabel(selectedRequest.status)}</span>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Service
                    </label>
                    <p className="text-white mt-1">{getCategoryName(selectedRequest.service_category)}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Location
                    </label>
                    <p className="text-white mt-1">{selectedRequest.location || 'Not specified'}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <DollarSign className="w-3 h-3" /> Budget
                    </label>
                    <p className="text-white mt-1">{selectedRequest.budget_range || 'Not specified'}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Preferred Dates
                    </label>
                    <p className="text-white mt-1">
                      {selectedRequest.preferred_dates?.length > 0 
                        ? selectedRequest.preferred_dates.join(', ') 
                        : 'Flexible'}
                    </p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-lg">
                    <label className="text-fp-textMuted text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Created
                    </label>
                    <p className="text-white mt-1">{formatDate(selectedRequest.created_at)}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-fp-textMuted text-xs block mb-2">Description</label>
                  <p className="p-4 bg-fp-bg rounded-lg text-white text-sm whitespace-pre-wrap">
                    {selectedRequest.description}
                  </p>
                </div>

                {/* Quote Section */}
                {selectedRequest.quote && (
                  <div className={`p-6 rounded-xl border ${
                    selectedRequest.quote.status === 'sent' 
                      ? 'bg-yellow-500/10 border-yellow-500/20' 
                      : selectedRequest.quote.status === 'approved'
                        ? 'bg-green-500/10 border-green-500/20'
                        : 'bg-fp-bg border-white/10'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className={`font-medium ${
                          selectedRequest.quote.status === 'sent' ? 'text-yellow-400' : 
                          selectedRequest.quote.status === 'approved' ? 'text-green-400' : 'text-white'
                        }`}>
                          Quote #{selectedRequest.quote.quote_number}
                        </p>
                        <p className="text-fp-textMuted text-sm capitalize">Status: {selectedRequest.quote.status}</p>
                      </div>
                      <p className="text-2xl font-bold text-white">
                        {formatCurrency(selectedRequest.quote.grand_total)}
                      </p>
                    </div>

                    {/* Line Items */}
                    <div className="space-y-2 mb-4">
                      {selectedRequest.quote.line_items?.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-fp-textMuted">
                            {item.description} × {item.quantity}
                          </span>
                          <span className="text-white">{formatCurrency(item.total)}</span>
                        </div>
                      ))}
                      <div className="border-t border-white/10 pt-2 mt-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-fp-textMuted">Subtotal</span>
                          <span className="text-white">{formatCurrency(selectedRequest.quote.subtotal)}</span>
                        </div>
                        {selectedRequest.quote.discount_amount > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-fp-textMuted">Discount</span>
                            <span className="text-green-400">-{formatCurrency(selectedRequest.quote.discount_amount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-fp-textMuted">GST ({selectedRequest.quote.gst_percent}%)</span>
                          <span className="text-white">{formatCurrency(selectedRequest.quote.gst_amount)}</span>
                        </div>
                      </div>
                    </div>

                    {selectedRequest.quote.notes && (
                      <p className="text-fp-textMuted text-sm mb-4 p-3 bg-white/5 rounded">
                        {selectedRequest.quote.notes}
                      </p>
                    )}

                    {/* Quote Actions */}
                    {selectedRequest.quote.status === 'sent' && (
                      <div className="flex gap-3">
                        <Button 
                          onClick={() => respondToQuote('approve')} 
                          className="btn-primary flex-1"
                          data-testid="approve-quote-btn"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve Quote
                        </Button>
                        <Button 
                          onClick={() => respondToQuote('revision')} 
                          variant="outline" 
                          className="border-white/10 flex-1"
                        >
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Request Revision
                        </Button>
                        <Button 
                          onClick={() => respondToQuote('reject')} 
                          variant="outline" 
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    {selectedRequest.quote.status === 'approved' && (
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span>Quote Approved</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Comments Section */}
                <div>
                  <label className="text-fp-textMuted text-xs block mb-3">Messages</label>
                  <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
                    {selectedRequest.comments?.filter(c => !c.is_internal).map((comment) => (
                      <div 
                        key={comment.id} 
                        className={`p-3 rounded-lg ${
                          comment.author_role === 'client' ? 'bg-primary/10 ml-8' : 'bg-fp-bg mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-sm font-medium">{comment.author_name}</span>
                          <span className="text-fp-textMuted text-xs">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-fp-textMuted text-sm">{comment.text}</p>
                      </div>
                    ))}
                    {(!selectedRequest.comments || selectedRequest.comments.filter(c => !c.is_internal).length === 0) && (
                      <p className="text-fp-textMuted text-sm text-center py-4">No messages yet</p>
                    )}
                  </div>
                  
                  {/* Add Comment */}
                  <div className="flex gap-2">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Type a message..."
                      className="bg-fp-bg border-white/10 text-white resize-none flex-1"
                      rows={2}
                      data-testid="client-comment-input"
                    />
                    <Button onClick={addComment} className="btn-primary self-end" data-testid="client-send-comment-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ClientLayout>
  );
};

export default ClientRequestsPage;
